import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiHeart, FiMenu, FiSearch, FiShoppingBag, FiUser, FiX } from "react-icons/fi";
import "../css/header.scss";
import { CartContext } from "./CartContext";
import productData, { formatPrice, siteConfig } from "../Datas/productData";

const megaColumns = [
  {
    title: "Featured",
    links: ["New Arrivals", "Bestsellers", "Summer Essentials", "Exclusive"],
  },
  {
    title: "Shoes",
    links: ["All Shoes", "Sneakers", "Loafers", "Cupsole", "Athletic"],
  },
  {
    title: "Clothing",
    links: ["All Clothing", "T-Shirts", "Knitwear", "Denim", "Jackets"],
  },
  {
    title: "Our world",
    links: ["About Us", "Quality & Craft", "Community", "Responsibility"],
  },
];

const moreLinks = [
  ["Privacy policy", "/privacy"],
  ["Cookie policy", "/cookies"],
  ["FAQs", "/faqs"],
  ["Terms & conditions", "/terms"],
  ["Shipping", "/shipping"],
  ["Returns & exchanges", "/returns"],
];

const routeMetadata = {
  "/": ["Footwear, apparel & culture", "Discover original footwear and apparel shaped by community, culture and considered craft."],
  "/shop": ["Shop", `Explore the complete ${siteConfig.brandName} footwear and apparel collection.`],
  "/shopDetails": ["Product", "Explore product details, sizing, materials and availability."],
  "/about": ["About", `Learn about the community, culture and considered craft behind ${siteConfig.brandName}.`],
  "/shipping": ["Shipping", "Delivery destinations, timings and shipping information."],
  "/returns": ["Returns", `Returns and exchanges information for your ${siteConfig.brandName} order.`],
  "/contact": ["Client service", `Contact the ${siteConfig.brandName} client service team.`],
  "/terms": ["Terms", `Terms and conditions for this demo ${siteConfig.brandName} storefront.`],
  "/privacy": ["Privacy", `Privacy information for this demo ${siteConfig.brandName} storefront.`],
  "/cookies": ["Cookie policy", `Cookie information and choices for the ${siteConfig.brandName} storefront.`],
  "/faqs": ["FAQs", `Frequently asked questions about shopping with ${siteConfig.brandName}.`],
  "/cartpage": ["Shopping bag", "Review the pieces saved in your shopping bag."],
  "/checkout": ["Checkout", "Complete delivery and payment details for this demo order."],
  "/paysuccess": ["Checkout complete", "Review the result of your demo checkout."],
};

const keepFocusInsideDialog = (event) => {
  if (event.key !== "Tab") return;

  const focusable = Array.from(
    event.currentTarget.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element.offsetParent !== null);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartItems = [], wishlist = [] } = useContext(CartContext);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeMega, setActiveMega] = useState("");
  const [query, setQuery] = useState("");
  const menuTriggerRef = useRef(null);
  const searchTriggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const previousPathRef = useRef(location.pathname);

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  const wishlistCount = wishlist.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setAnnouncementIndex((current) =>
        (current + 1) % siteConfig.announcements.length
      );
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const pathChanged = previousPathRef.current !== location.pathname;
    setMenuOpen(false);
    setSearchOpen(false);
    setActiveMega("");
    setQuery("");

    if (pathChanged) {
      window.scrollTo({ top: 0, behavior: "auto" });
      window.requestAnimationFrame(() => {
        document.getElementById("main-content")?.focus({ preventScroll: true });
      });
    }
    previousPathRef.current = location.pathname;

    const metadataKey = location.pathname.startsWith("/shopDetails/")
      ? "/shopDetails"
      : location.pathname;
    const [pageTitle, pageDescription] = routeMetadata[metadataKey] || [
      "Storefront",
      siteConfig.description,
    ];
    document.title = `${pageTitle} | ${siteConfig.brandName}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", pageDescription);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return undefined;

    const focusTimer = window.setTimeout(() => {
      if (searchOpen) searchInputRef.current?.focus();
      else document.querySelector(".mobile-menu__panel button")?.focus();
    }, 0);
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (searchOpen) {
        setSearchOpen(false);
        searchTriggerRef.current?.focus();
      } else {
        setMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen, searchOpen]);

  const matchingProducts = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return productData.slice(0, 4);
    const searchableQuery = normalized === "new arrivals" ? "new" : normalized;
    return productData
      .filter((product) =>
        [product.name, product.category, ...product.tags]
          .join(" ")
          .toLowerCase()
          .includes(searchableQuery)
      );
  }, [query]);
  const searchResults = matchingProducts.slice(0, 8);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.requestAnimationFrame(() => {
      document.getElementById("main-content")?.focus({ preventScroll: true });
    });
  };
  const completeNavigation = () => {
    setMenuOpen(false);
    setSearchOpen(false);
    setActiveMega("");
    setQuery("");
    scrollTop();
  };
  const closeMenu = () => {
    setMenuOpen(false);
    window.requestAnimationFrame(() => menuTriggerRef.current?.focus());
  };
  const closeSearch = () => {
    setSearchOpen(false);
    window.requestAnimationFrame(() => searchTriggerRef.current?.focus());
  };

  const openCollection = (label) => {
    const normalized = label.toLowerCase();
    if (normalized.includes("about") || normalized.includes("community") || normalized.includes("quality") || normalized.includes("responsibility")) {
      navigate("/about");
    } else if (normalized.includes("all shoes")) {
      navigate("/shop?type=Shoes");
    } else if (normalized.includes("all clothing")) {
      navigate("/shop?type=Clothing");
    } else {
      navigate(`/shop?category=${encodeURIComponent(label)}`);
    }
    completeNavigation();
  };

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="site-header" onMouseLeave={() => setActiveMega("")}>
        <div className="announcement-bar" aria-live="polite">
          <span key={announcementIndex} className="announcement-bar__message">
            {siteConfig.announcements[announcementIndex]}
          </span>
        </div>

        <div className="header-bar">
          <button
            ref={menuTriggerRef}
            className="header-icon header-menu-button"
            type="button"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <FiMenu aria-hidden="true" />
          </button>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {["MEN", "WOMEN"].map((item) => (
              <button
                type="button"
                key={item}
                className="desktop-nav__item"
                aria-expanded={activeMega === item}
                onFocus={() => setActiveMega(item)}
                onMouseEnter={() => setActiveMega(item)}
                onClick={() => setActiveMega(activeMega === item ? "" : item)}
              >
                {item}
              </button>
            ))}
            <Link className="desktop-nav__item desktop-nav__item--sale" to="/shop?category=Sale" onClick={completeNavigation}>
              SALE
            </Link>
            <Link className="desktop-nav__item" to="/about" onClick={completeNavigation}>
              OUR WORLD
            </Link>
            <a className="desktop-nav__item" href="#newsletter">
              REWARDS
            </a>
            <button
              type="button"
              className="desktop-nav__item"
              aria-expanded={activeMega === "MORE"}
              onFocus={() => setActiveMega("MORE")}
              onMouseEnter={() => setActiveMega("MORE")}
              onClick={() => setActiveMega(activeMega === "MORE" ? "" : "MORE")}
            >
              MORE
            </button>
          </nav>

          <Link className="brand-wordmark" to="/" onClick={scrollTop} aria-label={`${siteConfig.brandName} home`}>
            <img src="/wordmark.svg" alt="" />
          </Link>

          <nav className="header-actions" aria-label="Utility navigation">
            <button
              ref={searchTriggerRef}
              type="button"
              className="header-action header-action--search"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <FiSearch aria-hidden="true" />
              <span>SEARCH</span>
            </button>
            <Link className="header-action header-action--desktop" to="/contact" onClick={completeNavigation} aria-label="Client service">
              <FiUser aria-hidden="true" />
              <span>SERVICE</span>
            </Link>
            <Link className="header-action" to="/shop?view=wishlist" onClick={completeNavigation} aria-label={`Wishlist with ${wishlistCount} items`}>
              <FiHeart aria-hidden="true" />
              <span className="header-action--desktop">WISHLIST</span>
              {wishlistCount > 0 && <span className="header-count">{wishlistCount}</span>}
            </Link>
            <Link className="header-action" to="/cartpage" onClick={completeNavigation} aria-label={`Bag with ${cartCount} items`}>
              <FiShoppingBag aria-hidden="true" />
              <span className="header-action--desktop">BAG</span>
              <span className="header-count">({cartCount})</span>
            </Link>
          </nav>
        </div>

        <div className={`mega-menu ${activeMega ? "is-open" : ""}`} aria-hidden={!activeMega}>
          <div className="mega-menu__content">
            <div className="mega-menu__eyebrow">{activeMega || "SHOP"}</div>
            <div className={`mega-menu__columns ${activeMega === "MORE" ? "mega-menu__columns--more" : ""}`}>
              {activeMega === "MORE" ? (
                <div className="mega-menu__column">
                  <p>Information &amp; policies</p>
                  {moreLinks.map(([label, path]) => (
                    <Link key={path} to={path} onClick={completeNavigation}>{label}</Link>
                  ))}
                </div>
              ) : megaColumns.map((column) => (
                <div key={column.title} className="mega-menu__column">
                  <p>{column.title}</p>
                  {column.links.map((label) => (
                    <button key={label} type="button" onClick={() => openCollection(label)}>
                      {label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <button className="mega-menu__feature" type="button" onClick={() => openCollection("New Arrivals")}>
              <img src="/img/editorial/header-feature.jpg" alt="Original summer footwear and knitwear edit" />
              <span>THE NEW SEASON EDIT</span>
            </button>
          </div>
        </div>
      </header>

      <div className={`site-overlay mobile-menu ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen}>
        <div className="overlay-panel mobile-menu__panel" role="dialog" aria-modal="true" aria-label="Navigation menu" onKeyDown={keepFocusInsideDialog}>
          <div className="overlay-panel__header">
            <span className="brand-wordmark"><img src="/wordmark.svg" alt={siteConfig.brandName} /></span>
            <button type="button" onClick={closeMenu} aria-label="Close menu">
              <FiX aria-hidden="true" />
            </button>
          </div>
          <nav className="mobile-menu__nav" aria-label="Mobile navigation">
            <Link to="/shop?category=New%20Arrivals" onClick={completeNavigation}>New arrivals</Link>
            <Link to="/shop?type=Shoes" onClick={completeNavigation}>Shoes</Link>
            <Link to="/shop?type=Clothing" onClick={completeNavigation}>Clothing</Link>
            <Link className="mobile-menu__sale" to="/shop?category=Sale" onClick={completeNavigation}>Sale</Link>
            <Link to="/about" onClick={completeNavigation}>Our world</Link>
          </nav>
          <div className="mobile-menu__secondary">
            <Link to="/contact" onClick={completeNavigation}>Client service</Link>
            <Link to="/shipping" onClick={completeNavigation}>Shipping</Link>
            <Link to="/returns" onClick={completeNavigation}>Returns & exchanges</Link>
            <Link to="/privacy" onClick={completeNavigation}>Privacy policy</Link>
            <Link to="/cookies" onClick={completeNavigation}>Cookie policy</Link>
            <Link to="/faqs" onClick={completeNavigation}>FAQs</Link>
          </div>
          <p>{siteConfig.description}</p>
        </div>
        <button className="site-overlay__backdrop" type="button" aria-label="Close menu" onClick={closeMenu} />
      </div>

      <div className={`site-overlay search-overlay ${searchOpen ? "is-open" : ""}`} aria-hidden={!searchOpen}>
        <section className="overlay-panel search-panel" role="dialog" aria-modal="true" aria-label="Search products" onKeyDown={keepFocusInsideDialog}>
          <div className="search-panel__top">
            <label htmlFor="site-search">Search</label>
            <input
              ref={searchInputRef}
              id="site-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search…"
              autoComplete="off"
            />
            {query && <button type="button" onClick={() => setQuery("")}>CLEAR</button>}
            <button type="button" className="search-panel__close" onClick={closeSearch} aria-label="Close search">
              <FiX aria-hidden="true" />
            </button>
          </div>
          {!query && (
            <div className="popular-searches">
              <span>Popular searches</span>
              {["New Arrivals", "Loafers", "Cupsole", "Knitwear"].map((term) => (
                <button key={term} type="button" onClick={() => setQuery(term)}>{term}</button>
              ))}
            </div>
          )}
          <div className="search-results">
            <div className="search-results__heading">
              <span>{query ? "Results" : "Trending products"}</span>
              <span>{matchingProducts.length} items</span>
            </div>
            {searchResults.length ? (
              <div className="search-results__grid">
                {searchResults.map((product) => (
                  <Link key={product.id} to={`/shopDetails/${product.id}`} className="search-card" onClick={completeNavigation}>
                    <img src={product.image} alt={product.name} loading="lazy" />
                    <span>{product.name}</span>
                    <span>{formatPrice(product.price)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="search-results__empty">No products match “{query}”. Try another name, category or color.</p>
            )}
          </div>
          {query && matchingProducts.length > 0 && (
            <Link
              className="search-results__all"
              to={`/shop?search=${encodeURIComponent(query)}`}
              onClick={completeNavigation}
            >
              View all {matchingProducts.length} results
            </Link>
          )}
        </section>
        <button className="site-overlay__backdrop" type="button" aria-label="Close search" onClick={closeSearch} />
      </div>
    </>
  );
}

export default Header;
